import { useEffect, useRef, useState } from 'preact/hooks';

/**
 * Live input meter for the pre-join screen.
 *
 * "Design for the feeling of low latency … no spinner limbo" — the design
 * prompt. Seeing your own voice move a meter before you join is the fastest
 * possible proof that the audio path works, and it moves the most common
 * failure (wrong device, muted at OS level, permission denied) to *before* the
 * room rather than during it.
 *
 * Uses raw Web Audio rather than a LiveKit track so it can be torn down
 * completely before the real connection opens its own capture.
 */
export function MicCheck({
  onReady,
  active = true,
}: {
  onReady: (ok: boolean) => void;
  /**
   * Set false the instant joining begins.
   *
   * The meter holds a getUserMedia stream, and LiveKit is about to open its
   * own capture of the same device. On mobile a microphone can only be held
   * once, so keeping this alive through connect is a race that ends with the
   * publisher failing to get the mic.
   */
  active?: boolean;
}) {
  const [level, setLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const frame = useRef<number>(0);

  useEffect(() => {
    if (!active) return;
    let stream: MediaStream | undefined;
    let context: AudioContext | undefined;
    let cancelled = false;

    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (cancelled) return;
        context = new AudioContext();
        const source = context.createMediaStreamSource(stream);
        const analyser = context.createAnalyser();
        analyser.fftSize = 512;
        source.connect(analyser);
        const data = new Uint8Array(analyser.frequencyBinCount);

        const tick = () => {
          analyser.getByteTimeDomainData(data);
          // Peak deviation from silence (128), normalised.
          let peak = 0;
          for (const sample of data) peak = Math.max(peak, Math.abs(sample - 128));
          setLevel(Math.min(1, peak / 60));
          frame.current = requestAnimationFrame(tick);
        };
        tick();
        onReady(true);
      } catch (cause) {
        if (cancelled) return;
        const message =
          cause instanceof DOMException && cause.name === 'NotAllowedError'
            ? 'Microphone blocked. You can still listen — allow access to speak.'
            : 'No microphone found. You can still listen.';
        setError(message);
        onReady(false);
      }
    })();

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame.current);
      stream?.getTracks().forEach((track) => track.stop());
      void context?.close();
    };
  }, [onReady, active]);

  if (!active) return null;

  if (error) return <p class="t-foot">{error}</p>;

  return (
    <div class="miccheck">
      <div class="miccheck__meter" role="img" aria-label="Microphone input level">
        <div class="miccheck__fill" style={{ transform: `scaleX(${level})` }} />
      </div>
      <p class="t-foot">{level > 0.08 ? 'We can hear you.' : 'Say something to test your mic.'}</p>
    </div>
  );
}
